import { Component, computed, inject, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SHARED_IMPORTS } from '@shared/shared-providers';
import { LabelChipComponent } from '@shared/components/label-chip/label-chip.component';
import { LabelService } from '@shared/services/label.service';
import { ApprovalConfigService } from '@shared/services/approval-config.service';
import { Label, LabelDefinition } from '@shared/models/label.model';
import { AccessService } from '@cm/ui-modules';
import { AsyncTask } from '../../../../shared/services/async-task.service';
import { MediaLibraryService } from '../../../../shared/services/media-library.service';
import { SnackBarService } from '../../../../shared/services';

export interface MergePreviewDialogData {
  /** The completed `media-merge` task, carrying `result.previewUrl` and the draft's facts. */
  task: AsyncTask;
}

export interface MergePreviewDialogResult {
  /** True once the promotion has been ACCEPTED (202) — not once it has finished. */
  confirmed: boolean;
}

/** A label definition reduced to what the picker renders: its own labels, none system-managed. */
interface LabelGroup {
  definition: LabelDefinition;
  labels: Label[];
}

/**
 * Review step for a finished merge. The render produces a **draft**, not a library
 * asset: this dialog is the only place it can be watched, named, labelled and then
 * promoted (`POST …/confirm`) — or thrown away (`DELETE …/draft`).
 *
 * Every way out that is not "Save" means "throw this away", so the discard hangs off
 * `DialogRef.closed` rather than the Cancel button alone — see `dismissDraft` below.
 */
@Component({
  selector: 'app-merge-preview-dialog',
  templateUrl: './merge-preview-dialog.component.html',
  styleUrls: ['./merge-preview-dialog.component.scss'],
  standalone: true,
  imports: [SHARED_IMPORTS, LabelChipComponent],
})
export class MergePreviewDialogComponent {
  private dialogRef =
    inject<DialogRef<MergePreviewDialogResult, MergePreviewDialogComponent>>(DialogRef);
  readonly data = inject<MergePreviewDialogData>(DIALOG_DATA);
  private mediaLibraryService = inject(MediaLibraryService);
  private labelService = inject(LabelService);
  private snackBarService = inject(SnackBarService);
  private translate = inject(TranslateService);
  private accessService = inject(AccessService);
  private approvalConfig = inject(ApprovalConfigService);

  readonly name = signal<string>('');
  readonly selectedLabelUuids = signal<string[]>([]);
  readonly definitions = signal<LabelDefinition[]>([]);
  readonly saving = signal(false);

  /**
   * Set the moment `/confirm` succeeds. A plain field, not a signal: it is read from
   * the `closed` handler, which runs after this component has been destroyed.
   */
  private confirmed = false;
  private discardSent = false;

  readonly taskId = this.data.task.task_id;

  /**
   * The capability URL exactly as the backend minted it (`…/preview?key=…`). It is bound
   * straight to `<video src>`: a media element cannot send an Authorization header, and
   * routing it through HttpClient would only add one. Never rewrite or re-sign it.
   */
  readonly previewUrl: string = this.data.task.result?.previewUrl ?? '';

  readonly durationSeconds: number = this.data.task.result?.durationSeconds ?? 0;
  readonly width: number = this.data.task.result?.width ?? 0;
  readonly height: number = this.data.task.result?.height ?? 0;
  readonly sizeBytes: number = this.data.task.result?.sizeBytes ?? 0;

  /**
   * Owner is read-only on purpose: `MediaAsset.createdBy` is resolved server-side from
   * the caller's JWT and `MediaAssetRequest` has no owner field, so the merged asset is
   * owned by whoever confirms it. Showing it avoids a silent surprise.
   */
  readonly ownerName = computed(() => {
    const user = this.accessService.user();
    return user?.username || user?.email || '';
  });

  /**
   * System-managed definitions are excluded: the Compliance labels have exactly one
   * writer (`ComplianceStatusService`) and must never be attached by hand — the same
   * reasoning `BulkLabelButtonComponent` applies to its own menu. Tags first, colours
   * last, matching the order used everywhere else labels are listed.
   */
  readonly labelGroups = computed<LabelGroup[]>(() => {
    const defs = this.definitions().filter((d) => !d.systemManaged && d.labels?.length);
    const tags = defs.filter((d) => d.renderType !== 'COLOR');
    const colors = defs.filter((d) => d.renderType === 'COLOR');
    return [...tags, ...colors].map((definition) => ({
      definition,
      labels: definition.labels ?? [],
    }));
  });

  /**
   * A merged asset is a video, so it is regulated exactly when the tenant regulates video.
   * `ApprovalConfigService` falls back to "nothing is regulated" when
   * `/api/core/parameter/approval-configuration` is unavailable — which is the case on any
   * branch without the approve package (migration V085 seeds both the parameter and the
   * Compliance labels). The block therefore hides itself where compliance does not exist,
   * rather than promising a review that will never happen.
   */
  readonly showCompliance = computed(() => this.approvalConfig.isRegulated('video'));

  /**
   * Read-only for EVERYONE, including users with `approve` permission — not an oversight.
   * Compliance status is an outcome of the review workflow, never an input: cm-server-java's
   * `createMediaAsset` runs `filterOutSystemManagedLabels()` and silently strips every
   * Compliance uuid it is handed, and the only way the status moves is
   * `POST /approve/tasks/{uuid}/decision`. Rendering an editable control here would be a
   * control that appears to work and does nothing.
   */
  readonly complianceStatusName = ApprovalConfigService.STATUS_UNDER_REVIEW;

  readonly canSave = computed(() => !this.saving() && !!this.name().trim());

  readonly formattedDuration = computed(() => {
    const total = Math.round(this.durationSeconds);
    const seconds = total % 60;
    const minutes = Math.floor(total / 60) % 60;
    const hours = Math.floor(total / 3600);
    const pad = (value: number) => value.toString().padStart(2, '0');
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
  });

  readonly formattedSize = computed(() => {
    if (!this.sizeBytes) return '—';
    const mb = this.sizeBytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(this.sizeBytes / 1024))} KB`;
  });

  readonly formattedResolution = computed(() =>
    this.width && this.height ? `${this.width} × ${this.height}` : '—',
  );

  constructor() {
    this.name.set(this.data.task.metadata?.['name'] || this.data.task.displayLabel || '');
    this.labelService.getLabelDefinitions().subscribe({
      next: (definitions) => this.definitions.set(definitions ?? []),
      error: () => this.definitions.set([]),
    });

    // ONE discard path for every dismissal. Cancel, the header X, ESC and a backdrop
    // click all end in `DialogRef.close()`, and CDK emits `closed` for all four (it
    // disposes the overlay first, so this handler deliberately touches no template
    // state — only the captured service references, which are root singletons).
    // Hooking the button alone would leak a draft on ESC; `disableClose` would not
    // help either, since cm-ui-dialog's header closes programmatically.
    this.dialogRef.closed.subscribe(() => this.dismissDraft());
  }

  // ─── Labels ────────────────────────────────────────────────────────────────

  isSelected(labelUuid: string): boolean {
    return this.selectedLabelUuids().includes(labelUuid);
  }

  /**
   * Toggles one label. A SINGLE-selection definition keeps at most one of its own
   * labels, mirroring what the server enforces when labels are attached one by one.
   */
  toggleLabel(group: LabelGroup, label: Label): void {
    const selected = this.selectedLabelUuids();
    if (selected.includes(label.uuid)) {
      this.selectedLabelUuids.set(selected.filter((uuid) => uuid !== label.uuid));
      return;
    }
    const siblings = new Set(group.labels.map((item) => item.uuid));
    const kept =
      group.definition.selectionType === 'SINGLE'
        ? selected.filter((uuid) => !siblings.has(uuid))
        : selected;
    this.selectedLabelUuids.set([...kept, label.uuid]);
  }

  // ─── Actions ───────────────────────────────────────────────────────────────

  /**
   * Starts the promotion and hands it to the task toast; the dialog stays open only
   * if the server refuses outright (bad name, draft already consumed, dead token).
   *
   * The 202 means "accepted", not "in the library": the conversion behind it runs for
   * minutes. Waiting for it here is what produced a 504 and a dialog stuck on an
   * error while the asset quietly appeared in the library — so the dialog closes now
   * and the same task card reports the rest, refreshing the grid when it completes.
   */
  save(): void {
    if (!this.canSave()) return;
    this.saving.set(true);
    const name = this.name().trim();

    this.mediaLibraryService
      .confirmMerge(this.taskId, name, this.selectedLabelUuids())
      .subscribe({
        next: () => {
          // Before the close: `closed` fires dismissDraft(), which must not delete a
          // draft that is already on its way into the library.
          this.confirmed = true;
          this.mediaLibraryService.trackMergeSave(this.taskId, name);
          this.snackBarService.showWithText(
            this.translate.instant('ADMIN.medialibrary.mergePreview.saving'),
            'success',
          );
          this.dialogRef.close({ confirmed: true });
        },
        error: (error) => {
          console.error('[MergePreviewDialog] Failed to confirm merge:', error);
          this.saving.set(false);
          this.snackBarService.showWithText(
            this.translate.instant('ADMIN.medialibrary.mergePreview.saveError'),
            'fail',
          );
        },
      });
  }

  /** Cancel button — the draft is dropped by the shared `closed` handler. */
  cancel(): void {
    this.dialogRef.close({ confirmed: false });
  }

  /**
   * Deletes the draft unless it was just promoted. Idempotent on the client too: the
   * backend DELETE is idempotent, but the guard keeps a double close from firing twice.
   */
  private dismissDraft(): void {
    if (this.confirmed || this.discardSent) return;
    this.discardSent = true;
    this.mediaLibraryService.discardMergeDraft(this.taskId).subscribe({
      error: (error) =>
        console.error('[MergePreviewDialog] Failed to discard merge draft:', error),
    });
  }
}
